import * as React from 'react'
import { useEffect, useRef, useState } from 'react'
import useOutsideAlerter from '../../../../libs/use_outside_alerter'
import { ProjectGroupProps, SavedFilter, SavedFilterData } from '../types'
import { useSavedFilters } from './use_saved_filters'
import SavedFilterModal from './saved_filter_modal'
import I18n from '../../../../libs/i18n'
import { findMatchingFilter } from './filter_utils'

interface SavedFiltersDropdownProps {
  initialFilters: SavedFilter[]
  projectGroup: ProjectGroupProps
  onApplyFilter: (filter: SavedFilterData, options?: { updateUrl?: boolean }) => void
  currentFilterData: SavedFilterData
  clearFilterUrlParams: () => void
}

export default function SavedFiltersDropdown({
  initialFilters,
  projectGroup,
  onApplyFilter,
  currentFilterData,
  clearFilterUrlParams,
}: SavedFiltersDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingFilter, setEditingFilter] = useState<SavedFilter | null>(null)
  const [activeFilterId, setActiveFilterId] = useState<number | null>(null)

  const dropdownRef = useRef<HTMLDivElement>(null)
  useOutsideAlerter(dropdownRef, () => setIsOpen(false))

  const { savedFilters, createFilter, updateFilter, deleteFilter, isLoading } = useSavedFilters({
    initialFilters,
    projectGroupId: projectGroup.id,
  })

  useEffect(() => {
    const matchingFilter = findMatchingFilter(savedFilters, currentFilterData)
    setActiveFilterId(matchingFilter?.id ?? null)
  }, [savedFilters, currentFilterData])

  const handleFilterClick = (e: React.MouseEvent, filter: SavedFilter) => {
    e.preventDefault()
    onApplyFilter(filter.filter)
    setIsOpen(false)
  }

  const handleEditClick = (e: React.MouseEvent, filter: SavedFilter) => {
    e.stopPropagation()
    setEditingFilter(filter)
    setIsModalOpen(true)
    setIsOpen(false)
  }

  const handleAddNew = () => {
    setEditingFilter(null)
    setIsModalOpen(true)
    setIsOpen(false)
  }

  const handleSave = async (name: string, filter: SavedFilterData, isDefault: boolean) => {
    let savedFilter: SavedFilter
    if (editingFilter) {
      savedFilter = await updateFilter(editingFilter.id, name, filter, isDefault)
    } else {
      savedFilter = await createFilter(name, filter, isDefault)
    }
    onApplyFilter(filter)
    setActiveFilterId(savedFilter.id)
  }

  const handleDelete = async (id: number) => {
    await deleteFilter(id)
  }

  const handleResetFilters = (e: React.MouseEvent) => {
    e.preventDefault()
    clearFilterUrlParams()

    const defaultFilter: SavedFilterData = {
      status: projectGroup.available_statuses.filter((status) => status.is_default).map((status) => status.value),
    }
    onApplyFilter(defaultFilter, { updateUrl: false })
    setActiveFilterId(null)
    setIsOpen(false)
  }

  return (
    <div className="saved-filters-dropdown position-relative" ref={dropdownRef}>
      <button type="button" className="btn btn-secondary" onClick={() => setIsOpen(!isOpen)} disabled={isLoading}>
        {I18n.t('project_groups.overview.saved_filters.title')}
        <i className="fa fa-caret-down ml-1" aria-hidden="true" />
      </button>

      {isOpen && (
        <div className="dropdown-menu show" style={{ minWidth: '220px', right: 0, left: 'auto' }}>
          {savedFilters.length > 0 && (
            <>
              {savedFilters.map((filter) => {
                const isActive = filter.id === activeFilterId
                const maxLength = 50
                const displayName = filter.name.length > maxLength
                  ? `${filter.name.substring(0, maxLength)}...`
                  : filter.name
                return (
                  <div
                    key={filter.id}
                    className="dropdown-item d-flex align-items-center justify-content-between"
                    title={filter.name}
                  >
                    <button
                      type="button"
                      className={`btn btn-link p-0 text-left flex-grow-1 ${isActive ? 'font-weight-bold' : ''}`}
                      onClick={(e) => handleFilterClick(e, filter)}
                    >
                      {filter.is_default && '* '}
                      {displayName}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary ml-2"
                      onClick={(e) => handleEditClick(e, filter)}
                      title={I18n.t('project_groups.overview.saved_filters.edit')}
                      aria-label={I18n.t('project_groups.overview.saved_filters.edit')}
                    >
                      <i className="fa fa-pencil" aria-hidden="true" />
                    </button>
                  </div>
                )
              })}
              <div className="dropdown-divider" />
            </>
          )}
          <button type="button" className="dropdown-item" onClick={handleResetFilters}>
            {I18n.t('project_groups.overview.saved_filters.reset')}
          </button>
          <button type="button" className="dropdown-item" onClick={handleAddNew}>
            {I18n.t('project_groups.overview.saved_filters.add_new')}
          </button>
        </div>
      )}

      <SavedFilterModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        filter={editingFilter}
        projectGroup={projectGroup}
        currentFilterData={currentFilterData}
        onSave={handleSave}
        onDelete={handleDelete}
      />
    </div>
  )
}
